<template>
  <div class="area-selection">
    <Popup ref="popup" v-model="show" position="bottom">
      <div class="area-selection__popup">
        <div v-if="loading && !options.length" class="area-selection__state">
          {{ $t('loading') }}
        </div>
        <div v-else-if="errorMessage && !options.length" class="area-selection__state">
          <div class="area-selection__state-text">{{ errorMessage }}</div>
          <div class="btn-primary area-selection__retry" @click="reload">
            {{ $t('retry') }}
          </div>
        </div>
        <div v-else>
          <div v-if="errorMessage && options.length" class="area-selection__notice">
            <div class="area-selection__notice-text">{{ errorMessage }}</div>
            <div class="btn-primary area-selection__retry" @click="reload">
              {{ $t('retry') }}
            </div>
          </div>
          <van-cascader
            v-model="selectedValue"
            :field-names="fieldNames"
            :placeholder="$t('select')"
            :options="options"
            @close="show = false"
            @change="onChange"
            @finish="onFinish"
          >
            <template #title>
              <div class="title">
                {{ $t('addressPop.regionsPlaceholder') }}
              </div>
            </template>
          </van-cascader>
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
import i18n from '@/i18n';
import { Popup, Cascader } from 'vant';
import { LANGUAGE } from '@/common/constants';
import { getShippingAddressRegions } from '@/api/common';
import {
  buildShippingRegionCacheKey,
  getShippingRegionCache,
  setShippingRegionCache,
} from '@/utils/addressRegionCache';

export default {
  name: 'AreaSelection',
  components: {
    Popup,
    [Cascader.name]: Cascader,
  },
  props: {
    defaultValue: {
      type: [Number, String],
      default: ''
    },
    defaultProvinceCode: {
      type: [Number, String],
      default: ''
    },
    defaultCityCode: {
      type: [Number, String],
      default: ''
    },
    defaultRegionCode: {
      type: [Number, String],
      default: ''
    },
  },
  computed: {
    fieldNames() {
      let fieldNames = { text: 'name_en', value: 'code', children: 'children' };
      if (i18n.locale === LANGUAGE.ph) {
        fieldNames.text = 'name_ph';
      } else if (i18n.locale === LANGUAGE.id) {
        fieldNames.text = 'name_id';
      }
      return fieldNames;
    },
    normalizedProvinceCode() {
      return this.normalizeCode(this.defaultProvinceCode);
    },
    normalizedCityCode() {
      return this.normalizeCode(this.defaultCityCode);
    },
    normalizedRegionCode() {
      return this.normalizeCode(this.defaultRegionCode);
    },
  },
  data() {
    return {
      show: false,
      loading: false,
      errorMessage: '',
      options: [],
      initializedKey: '',
      selectedValue: '',
    };
  },
  watch: {
    defaultValue: {
      immediate: true,
      handler(value) {
        this.selectedValue = this.normalizeCode(value);
      },
    },
  },
  methods: {
    normalizeCode(value) {
      const code = parseInt(value, 10);
      return Number.isNaN(code) ? '' : code;
    },
    open() {
      this.selectedValue = this.normalizeCode(this.defaultValue);
      this.show = true;
      this.loadInitialOptions();
    },
    close() {
      this.show = false;
    },
    reload() {
      this.loadInitialOptions(true);
    },
    onChange(data) {
      const selectedOptions = data.selectedOptions || [];
      const province = selectedOptions[0] || {};
      const city = selectedOptions[1] || {};
      this.selectedValue = this.normalizeCode(data.value);
      if (data.tabIndex === 0 && province.code) {
        this.loadCityOptions(province.code).catch((error) => this.handleLoadError(error));
      } else if (data.tabIndex === 1 && province.code && city.code) {
        this.loadRegionOptions(province.code, city.code).catch((error) => this.handleLoadError(error));
      }
    },
    onFinish(data) {
      this.close();
      const selectedArea = (data.selectedOptions || []).map((item) => {
        let name = item.name_en;
        if (i18n.locale === LANGUAGE.ph) {
          name = item.name_ph;
        } else if (i18n.locale === LANGUAGE.id) {
          name = item.name_id;
        }
        return {
          ...item,
          name: name,
        };
      });
      this.$emit('confirm', {
        selectedArea: selectedArea,
        regionCode: data.value
      });
    },
    async loadInitialOptions(forceRefresh = false) {
      const cacheKey = buildShippingRegionCacheKey(this.normalizedProvinceCode, this.normalizedCityCode);
      if (!forceRefresh && this.initializedKey === cacheKey && this.options.length) {
        return;
      }
      this.loading = true;
      this.errorMessage = '';
      try {
        this.options = await this.loadRegionNodes('', '', forceRefresh);
        if (this.normalizedProvinceCode) {
          await this.loadCityOptions(this.normalizedProvinceCode, forceRefresh);
          if (this.normalizedCityCode) {
            await this.loadRegionOptions(this.normalizedProvinceCode, this.normalizedCityCode, forceRefresh);
          }
        }
        this.initializedKey = cacheKey;
      } catch (error) {
        this.options = [];
        this.errorMessage = error?.message || this.$t('systemError');
        this.$toast(this.errorMessage);
      } finally {
        this.loading = false;
      }
    },
    async loadCityOptions(provinceCode, forceRefresh = false) {
      const nodes = await this.loadRegionNodes(provinceCode, '', forceRefresh);
      const province = this.findNodeByCode(this.options, provinceCode);
      if (province) {
        this.$set(province, 'children', nodes);
      }
      this.errorMessage = '';
      return nodes;
    },
    async loadRegionOptions(provinceCode, cityCode, forceRefresh = false) {
      const nodes = await this.loadRegionNodes(provinceCode, cityCode, forceRefresh);
      const province = this.findNodeByCode(this.options, provinceCode);
      const city = province ? this.findNodeByCode(province.children || [], cityCode) : null;
      if (city) {
        this.$set(city, 'children', nodes);
      }
      this.errorMessage = '';
      return nodes;
    },
    async loadRegionNodes(provinceCode = '', cityCode = '', forceRefresh = false) {
      const cached = forceRefresh ? null : getShippingRegionCache(provinceCode, cityCode);
      if (cached) {
        return cached;
      }
      const response = await getShippingAddressRegions(
        {
          provinceCode,
          cityCode,
        },
        {
          _showLoading: false,
        },
      );
      const success = response && (response.code === 200 || response.code === 'Success');
      if (!success) {
        throw new Error(response?.message || response?.msg || this.$t('systemError'));
      }
      const withChildren = !cityCode;
      const nodes = Array.isArray(response.data)
        ? response.data.map((item) => this.normalizeRegionNode(item, withChildren))
        : [];
      setShippingRegionCache(provinceCode, cityCode, nodes);
      return nodes;
    },
    handleLoadError(error) {
      const message = error?.message || this.$t('systemError');
      this.errorMessage = message;
      this.$toast(message);
    },
    normalizeRegionNode(item = {}, withChildren = false) {
      const node = {
        name_en: item.name_en || '',
        name_id: item.name_id || '',
        name_ph: item.name_ph || '',
        code: this.normalizeCode(item.code),
        psgc_code: this.normalizeCode(item.psgc_code || item.code),
      };
      if (withChildren) {
        node.children = [];
      }
      return node;
    },
    findNodeByCode(nodes = [], code = '') {
      const normalizedCode = this.normalizeCode(code);
      if (!normalizedCode) {
        return null;
      }
      return nodes.find((item) => this.normalizeCode(item.code) === normalizedCode) || null;
    }
  }
}
</script>

<style lang="less" scoped>
.van-popup {
  background: none;
}

.area-selection {
  &__popup {
    padding-top: 30px;
    border-radius: var(--border-radius-base) var(--border-radius-base) 0 0;
    background: #ffffff;
  }

  &__state {
    padding: 48px 32px 56px;
    text-align: center;
    font-size: 28px;
    line-height: 40px;
    color: var(--text-color-gray);
  }

  &__state-text {
    margin-bottom: 24px;
  }

  &__notice {
    margin: 0 32px 16px;
    padding: 16px 20px;
    border-radius: 14px;
    background: #F7F8FC;
    color: var(--text-color-gray);
    font-size: 24px;
    line-height: 32px;
  }

  &__notice-text {
    margin-bottom: 12px;
  }

  &__retry {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 180px;
    padding: 0 24px;
    border-radius: 14px;
  }

  /deep/ .van-cascader {
    &__option {
      font-size: 30px;
      line-height: 40px;
    }
  }

  .title {
    font-weight: 500;
    font-size: 32px;
    line-height: 37px;
  }
}
</style>
